import { getToken } from '@core/request/token'
import { load } from 'cheerio'

export function getImgUrl(id) {
  if (id === '') {
    return ''
  }
  return `${
    process.env.VUE_APP_TCLOUD_BASE_API
  }/tcloud-fs/fs/readImage?fileId=${id}&X-Token=${getToken()}`
}

// 打字机效果
export function typewriterEffect(
  text: string,
  callback: (currentText: string) => void,
  resultCallback?: () => void,
  speed = 100,
) {
  let index = 0
  let animationFrameId: number | null = null
  let isAborted = false
  let lastTime = 0 // 新增时间戳记录

  const controller = {
    abort: () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId)
        animationFrameId = null
        isAborted = true
      }
    },
  }

  const typeNext = (timestamp: number) => {
    if (isAborted) return

    // 计算时间差
    const delta = timestamp - lastTime
    if (delta < speed) {
      animationFrameId = requestAnimationFrame(typeNext)
      return
    }

    // 更新最后执行时间
    lastTime = timestamp

    if (index >= text.length) {
      resultCallback?.()
      return
    }

    const charCount = Math.floor(Math.random() * 3) + 1
    const currentSlice = text.slice(index, index + charCount)

    callback(currentSlice)
    index += charCount

    animationFrameId = requestAnimationFrame(typeNext)
  }

  animationFrameId = requestAnimationFrame(typeNext)
  return controller
}
/**
 * 获取元素下的所有文本节点
 * @param element 元素
 * @returns 元素下的所有文本节点
 */
export function getTextNodes(element) {
  if (!element) return [] // 添加空值校验

  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, {
    acceptNode: node =>
      node.textContent?.trim() // 添加可选链操作符
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT,
  })
  const textNodes = []
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode)
  }
  return textNodes
}

export function animateProgress(handler: (progress: number) => boolean, interval = 50) {
  let lastTime = 0
  let animationFrameId: number
  let isRunning = true

  const animate = (timestamp: number) => {
    if (!isRunning) return

    if (timestamp - lastTime > interval) {
      const shouldContinue = handler(timestamp)
      lastTime = timestamp

      if (!shouldContinue) {
        isRunning = false
        return
      }
    }

    animationFrameId = requestAnimationFrame(animate)
  }

  animationFrameId = requestAnimationFrame(animate)

  return () => {
    isRunning = false
    cancelAnimationFrame(animationFrameId)
  }
}

export const htmlParseData = (html: string, model?: string) => {
  if (!html) return ''

  // 1. 加载 HTML (decodeEntities: false 防止中文被转义为编码)
  const $ = load(html)

  // 2. 定义需要彻底移除的标签列表 (包含内容一起移除)
  // 这里涵盖了你要求的 svg, canvas, video, header, 以及原逻辑中的 a 标签
  const removeSelectors = [
    'script',
    'style',
    'noscript',
    'iframe',
    'svg',
    'canvas',
    'video',
    'audio',
    'header',
    'footer', // 建议加上 footer
    'a', // 原逻辑：移除 a 标签及其内容
    'img[src^="data:image/svg+xml"]', // 原逻辑：移除 svg base64 图片
  ]

  // 3. 模型特定的清理逻辑 (DOM 阶段处理)
  if (model === 'wenXin') {
    removeSelectors.push('[class*="video-scroll-wrapper_"]', '[class*="cs-hover-menu"]')
  } else if (model === 'yuanBao') {
    removeSelectors.push('[class*="hyc-common-markdown__ref-list"]')
  }

  // 通义千问的引用链接清理 (通用处理，匹配 class 前缀)
  // 原正则：<([^>]+) class="mdReference--[^"]*"...
  removeSelectors.push('[class*="mdReference--"]')

  // 执行统一移除
  $(removeSelectors.join(',')).remove()

  // 4. 获取 HTML 字符串并进行最后的文本级清洗
  // 此时得到的 html 已经是干净的 DOM 结构，没有多余的标签
  let cleanHtml = $.html()

  // 5. 正则处理属性和空白 (这一步正则处理比 DOM 遍历更快)
  cleanHtml = cleanHtml
    // 移除注释
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/\s+(class|style|data-[\w-]+|width|height|id)\s*=\s*(["'])(?:(?!\2).)*\2/gi, '')
    // 处理腾讯元宝的特殊字符 span
    .replace(/<span>•<\/span>/g, '')
    // 压缩空白字符 (将换行和多空格合并为一个空格)
    .replace(/\s{2,}/g, ' ')
    // 移除标签之间多余的空格 (如 > < 变成 ><)
    .replace(/>\s+</g, '><')

  // 如果你需要进一步移除空的 div 或 span (可选优化)
  // cleanHtml = cleanHtml.replace(/<(div|span)[^>]*>\s*<\/\1>/gi, '')

  return cleanHtml
}

export function htmlToPlainText(html) {
  return html
    .replace(/<br\s*\/?>\n?/gi, '\n') // 处理换行标签
    .replace(/<\/p>\n?/gi, '\n') // 处理段落结尾
    .replace(/<[^>]+>/g, '') // 移除所有 HTML 标签
    .replace(/\n{3,}/g, '\n\n') // 压缩连续空行
    .trim()
}

export function copy(editorHtml) {
  // 使用DOMParser去除外层html/body标签
  const doc = new DOMParser().parseFromString(editorHtml, 'text/html')
  const bodyContent = doc.body.innerHTML
  // 提取纯文本内容
  const plainText = htmlToPlainText(bodyContent)
  // 创建包含纯文本和HTML格式的ClipboardItem
  const clipboardItem = new ClipboardItem({
    'text/html': new Blob([bodyContent], { type: 'text/html' }),
    'text/plain': new Blob([plainText], { type: 'text/plain' }),
  })

  return navigator.clipboard.write([clipboardItem])
}

export async function copyText(text: string) {
  try {
    await navigator.clipboard.writeText(text)
  } catch {
    const ta = document.createElement('textarea')
    ta.value = text
    ta.style.position = 'fixed'
    ta.style.opacity = '0'
    document.body.appendChild(ta)
    ta.select()
    document.execCommand('copy')
    document.body.removeChild(ta)
  }
}

export const findTextMatches = (text: string, container: HTMLElement = document.body) => {
  const textNodes = getTextNodes(container)
  const matches = {
    found: false,
    firstElement: null as HTMLElement | null,
    elements: [] as HTMLElement[],
    textNodes: [] as Text[],
  }

  textNodes.forEach(node => {
    if (node.textContent?.includes(text)) {
      matches.found = true
      // eslint-disable-next-line prefer-destructuring
      const parentElement = node.parentElement
      if (parentElement && !matches.elements.includes(parentElement)) {
        matches.elements.push(parentElement)
        matches.textNodes.push(node)
        if (!matches.firstElement) {
          matches.firstElement = parentElement
        }
      }
    }
  })
  if (matches.found && matches.firstElement && matches.textNodes.length) {
    window.getSelection()?.removeAllRanges()
    // 创建选中范围
    const range = document.createRange()
    const firstNode = matches.textNodes[0]
    const startIndex = firstNode.textContent.indexOf(text)
    range.setStart(firstNode, startIndex)
    range.setEnd(firstNode, startIndex + text.length)

    // 滚动到第一个匹配项
    matches.firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const selection = window.getSelection()
    selection?.addRange(range)
  }
  return matches // 添加返回语句
}

export const isMac = () => {
  // 组合检测平台和用户代理
  return (
    // 优先检测只读属性 navigator.platform
    (navigator.platform.includes('Mac') ||
      // 保留原始 userAgent 检测作为备用
      /macintosh|mac os x/i.test(navigator.userAgent)) &&
    // 排除 iPad 设备（iPad 的桌面模式会返回 Mac 平台）
    !(navigator.maxTouchPoints > 2)
  )
}
